class SlickMediaGalleryProduct extends HTMLElement {
  constructor() {
    super();

    // Core refs
    this.slideshow = this.querySelector(".slideshow");
    this.photoSlides = this.slideshow?.querySelectorAll(".photo-slide") || [];

    this.slickGalleryHolder = this.querySelector(".slick-media-gallery-holder-inner");
    this.slickGalleryNav = this.querySelector(".slick-navigation");
    this.loadingSquare = this.querySelector(".loading-square");

    // Bind helpers once
    this.navItemClick = this.navItemClick.bind(this);
    this.refreshNavButtons = this.refreshNavButtons.bind(this);
    this.isSlickReady = this.isSlickReady.bind(this);

    // 1) Capture initial nav buttons BEFORE slick
    this.refreshNavButtons();

    // 2) Reveal hero immediately: remove spinner and outer holder hidden class.
    //    CSS (section-main-product.css) ensures only .slide:first-child is visible
    //    pre-init, keeping slides 2–N hidden until Slick adds slick-initialized.
    this.loadingSquare?.remove();
    this.slickGalleryHolder?.classList?.remove("hidden");

    // 3) Defer Slick init to idle so the hero can paint before DOM work begins.
    //    timeout:500 ensures init always runs within 500ms on busy pages.
    //    setTimeout fallback for Safari < 16 (no requestIdleCallback support).
    const initFn = this.initSlideshowSlick.bind(this);
    requestAnimationFrame(() => {
      if (typeof requestIdleCallback === "function") {
        requestIdleCallback(initFn, { timeout: 500 });
      } else {
        setTimeout(initFn, 0);
      }
    });

    // 4) Arrow hitboxes (guard Slick calls)
    const arrowHitboxes = this.querySelectorAll(".arrow-hitbox");
    arrowHitboxes.forEach((hitbox) => {
      hitbox.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!this.isSlickReady()) return;
        const direction = hitbox.dataset.click;
        if (direction === "prev") $(this.slideshow).slick("slickPrev");
        else if (direction === "next") $(this.slideshow).slick("slickNext");
      });
    });
  }

  // Is the Slick instance alive and usable?
  isSlickReady() {
    const $el = $(this.slideshow);
    if (!$el.length || !$el.hasClass("slick-initialized")) return false;
    const inst = $el.slick("getSlick");
    return !!(inst && !inst.destroyed && inst.$slides); // $slides is non-null when alive
  }

  // (Re)query nav buttons and (re)bind handlers safely
  refreshNavButtons() {
    this.navClick = this.querySelectorAll(".slide-nav-button");
    this.navClick.forEach((item) => {
      item.removeEventListener?.("click", this.navItemClick);
      item.addEventListener("click", this.navItemClick);
    });
  }

  navItemClick(e) {
    if (!this.navClick?.length) return;

    this.navClick.forEach((item) => item?.classList?.remove?.("border"));

    const btn = e.currentTarget?.closest(".slide-nav-button");
    if (!btn) return;

    btn.classList.add("border");

    const slideNumber = Number(btn.dataset.slideNum);
    if (Number.isFinite(slideNumber) && this.isSlickReady()) {
      $(this.slideshow).slick("slickGoTo", slideNumber - 1);
    }
  }

  // Safely highlight a nav item by index (handles stale/missing)
  highlightNavItem(item_num) {
    const len = this.navClick?.length ?? 0;
    if (!len) return;

    // Coerce & clamp
    let i = Number(item_num);
    if (!Number.isFinite(i)) i = 0;
    if (i < 0) i = 0;
    if (i >= len) i = len - 1;

    for (const item of this.navClick) {
      item?.classList?.remove?.("border");
    }

    const el = this.navClick[i];
    if (el?.classList) el.classList.add("border");
  }

  initSlideshowSlick() {
    const arrows_enable = this.dataset.arrows === "true";

    const slick_options = {
      dots: this.dataset.dots === "true",
      arrows: arrows_enable,
      autoplay: this.dataset.autoplay === "true",
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed),
      draggable: this.dataset.draggable === "true",
      speed: parseInt(this.dataset.speed),
      infinite: this.dataset.infinite === "true",
      fade: this.dataset.fade === "true",
      slidesToShow: parseInt(this.dataset.slidesToShow),
      slidesToScroll: parseInt(this.dataset.slidesToScroll),
      centerMode: this.dataset.centerMode === "true",
      accessibility: this.dataset.accessibility === "true",
      adaptiveHeight: this.dataset.adaptiveHeight === "true",
      cssEase: this.dataset.easing,
      useTransform: this.dataset.useTransform === "true",
      responsive: [
        {
          breakpoint: 992,
          settings: {
            slidesToShow: parseInt(this.dataset.slidesToShowLarge),
            slidesToScroll: parseInt(this.dataset.slidesToScrollLarge),
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: parseInt(this.dataset.slidesToShowTablets),
            slidesToScroll: parseInt(this.dataset.slidesToScrollTablets),
          },
        },
        {
          breakpoint: 576,
          settings: {
            slidesToShow: parseInt(this.dataset.slidesToShowSmall),
            slidesToScroll: parseInt(this.dataset.slidesToScrollSmall),
          },
        },
      ],
    };

    const prev = this.querySelector(".slideshow-prev");
    const next = this.querySelector(".slideshow-next");
    if (prev) slick_options.prevArrow = prev;
    if (next) slick_options.nextArrow = next;

    this.slickGalleryNav?.classList?.remove("hidden");

    const $el = $(this.slideshow);

    // Avoid double init; if somehow initialized, just update layout
    if ($el.hasClass("slick-initialized")) {
      $el.slick("setPosition");
    } else {
      $el
        // Namespaced events so we can cleanly .off('.smg')
        .on("init.smg reInit.smg", (_e, slick) => {
          if (!slick || slick.destroyed || !slick.$slides) return;
          this.refreshNavButtons();
          this.highlightNavItem(slick.currentSlide ?? 0);
        })
        .on("breakpoint.smg setPosition.smg", (_e, slick) => {
          // During breakpoints Slick may be mid-teardown—guard
          if (!slick || slick.destroyed || !slick.$slides) return;
          this.refreshNavButtons();
        })
        .on("beforeChange.smg", (_e, slick, _cur, nextSlide) => {
          if (!slick || slick.destroyed || !slick.$slides) return;
          this.highlightNavItem(nextSlide);
        })
        .slick(slick_options);
    }
  }

  // If this element is ever removed/re-added dynamically
  disconnectedCallback() {
    try {
      const $el = $(this.slideshow);
      if ($el.hasClass("slick-initialized")) {
        $el.off(".smg");           // remove our namespaced handlers
        // optional: $el.slick('unslick'); // only if you truly want to tear it down
      } else {
        $el.off(".smg");
      }
    } catch {}
  }
}

customElements.define("slick-media-gallery-product", SlickMediaGalleryProduct);
